import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, p, prettyScroll} from "@pg-design/helpers-css-strings";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {Text} from "@pg-design/text-module";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../property_list/hooks/use_property_list";
import {PropertyListTable} from "../../property_list/PropertyListTable";
import {PropertyListTabSwitcher, PropertyListType} from "../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../property_list/PropertyListTileList";
import {closeQuickViewHit, openQuickViewHit} from "../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {MapOfferPropertyFilters} from "../components/map_offer/MapOfferPropertyFilters";
import {MapOfferSidebarQuickView} from "../map/components/MapOfferSidebarQuickView";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {hitGTMPropertySelectItem} from "../tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {hitOfferModalArrowGtm} from "../tracking/offer_modal_arrow_gtm_events";
import {IOfferDetail} from "../types/IOfferDetail";

interface IProps {
    offer: IMapOfferSidebar;
    latestQuery: ILatestQuery;
    propertyList: IPropertyListObj;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
    gtmMeta: {
        listId: string;
        listName: string;
    };
}

export const OfferSidebarPropertiesList = (props: IProps) => {
    const {properties, filters, pageCount, onPageChange: onPropertyListPageChange, onSortChange, sort, page, requestState, pageIsLoading} = props.propertyList;
    const propertiesRequestState = useSelector((store: IRPStore) => store.offerDetail.properties.modal.requestState);
    const mapListRequestState = useSelector((store: IRPStore) => store.offerListMap.list.requestState);

    const {isMobile} = useUserDevice();
    const theme = useTheme();

    // QuickView Variant
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    useEffect(() => {
        // QuickView Variant
        //Close QW if user change property
        if (isQuickViewOpen) {
            setIsQuickViewOpen(false);
        }

        //When filters are changing set current page from n to 1
        if (page.currentPage !== 1) {
            page.setCurrentPage(1);
        }
    }, [props.offer.id, mapListRequestState]);

    const onPageChange = (newPage: number) => {
        const direction = newPage > page.currentPage ? "right" : "left";
        const listType = props.propertyListType === PropertyListType.TILES ? "tiles" : "table";

        hitOfferModalArrowGtm(direction, listType);

        return onPropertyListPageChange(newPage, filters.currentFilters);
    };

    const handleQuickViewOpen = (index: number) => {
        const currentOffer = props.offer;
        const currentProperty = properties[index];

        setIsQuickViewOpen(true);
        setSelectedIndex(index);
        openQuickViewHit(currentOffer.id, currentProperty.id);

        hitGTMPropertySelectItem({
            offer: currentOffer,
            property: currentProperty,
            ...props.gtmMeta
        });
    };

    const handleQuickViewClose = (): void => {
        setIsQuickViewOpen(false);
        setSelectedIndex(null);
        closeQuickViewHit();
    };

    const handleQuickViewOpenByProperty = (property: IPropertyListProperty) => {
        const index = properties.findIndex(({id}) => id === property.id);
        if (index !== -1) {
            handleQuickViewOpen(index);
        }
    };

    return (
        <>
            <PropertyListTableHolder isQuickViewOpen={isQuickViewOpen}>
                <div css={controlsWrapperStyle}>
                    <MapOfferPropertyFilters offerType={props.offer.type} propertyList={props.propertyList} />
                    <PropertyListTabSwitcher
                        currentPropertyListType={props.propertyListType}
                        setCurrentPropertyListType={props.onPropertyListTypeClick}
                        buttonCss={listTabSwitchButtonStyle}
                        css={propertyListTabSwitcherStyle}
                    />
                </div>
                {properties.length > 0 && (
                    <>
                        {props.propertyListType === PropertyListType.TABLE && (
                            <PropertyListTable
                                requestState={requestState}
                                offer={props.offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value)}
                                onPageChange={onPageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={[modalPropertyList]}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleDivClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground={theme.colors.gray[100]}
                                gtmMeta={props.gtmMeta}
                            />
                        )}

                        {props.propertyListType === PropertyListType.TILES && (
                            <PropertyListTileList
                                requestState={requestState}
                                properties={properties}
                                offer={props.offer as unknown as IOfferDetail}
                                css={modalPropertyList}
                                gridLayoutCols={{md: 2, lg: 2}}
                                onPropertyTileClick={handleQuickViewOpenByProperty}
                                propertyBoxComponent="div"
                                gtmMeta={props.gtmMeta}
                            />
                        )}

                        {pageCount > 0 && (
                            <div css={paginationHolder}>
                                <Pagination
                                    onPageChange={onPageChange}
                                    pageCount={pageCount}
                                    currentPage={page.currentPage}
                                    size={isMobile ? PaginationSize.SM : PaginationSize.MD}
                                    siblingsCount={isMobile ? 0 : 1}
                                />
                            </div>
                        )}
                    </>
                )}
                {properties?.length === 0 && requestState === RequestState.Success && (
                    <Text variant="body_copy_0" align="center" css={noResultsWrapperStyle}>
                        Brak wyników dla szukanej frazy
                    </Text>
                )}
            </PropertyListTableHolder>
            {/*QuickView Variant*/}
            {isQuickViewOpen && selectedIndex !== null && (
                <MapOfferSidebarQuickView
                    handleQuickViewClose={handleQuickViewClose}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    propertiesRequestState={propertiesRequestState}
                    offer={props.offer}
                />
            )}
        </>
    );
};

const controlsWrapperStyle = css`
    ${p(2, 0)};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${calculateRemSize(4)};
`;

const listTabSwitchButtonStyle = css`
    ${p(1)};
`;

const PropertyListTableHolder = styled.div<{isQuickViewOpen: boolean}>`
    flex-grow: 1;
    flex-shrink: 1;
    overflow-x: hidden;
    padding-top: 1.5rem;
    ${prettyScroll()};
    ${({isQuickViewOpen}) =>
        isQuickViewOpen &&
        css`
            display: none;
        `};
`;

const modalPropertyList = css`
    margin-bottom: 0 !important;
`;

const propertyListTabSwitcherStyle = css`
    flex: 0 0 auto;
`;

const paginationHolder = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    background-color: ${theme.colors.gray[100]};
    width: 100%;
    padding: 1.6rem;
    flex-shrink: 0;
    z-index: 1;
    position: relative;

    @media (min-width: ${theme.breakpoints.sm}) {
        position: sticky;
        bottom: 0;
        padding: 0.6rem 0 0;
    }
`;

const noResultsWrapperStyle = css`
    ${p(6, 2, 2, 2)};
`;
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, p, prettyScroll} from "@pg-design/helpers-css-strings";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {Text} from "@pg-design/text-module";
import {RequestState} from "@pg-mono/request-state";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {ApplicationSourceSection} from "../../application/utils/ApplicationSource";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {IPropertyListObj} from "../../property_list/hooks/use_property_list";
import {PropertyListTable} from "../../property_list/PropertyListTable";
import {PropertyListTabSwitcher, PropertyListType} from "../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../property_list/PropertyListTileList";
import {closeQuickViewHit, openQuickViewHit} from "../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {ILatestQuery} from "../../utils/latest_query";
import {MapOfferPropertyFilters} from "../components/map_offer/MapOfferPropertyFilters";
import {MapOfferSidebarQuickView} from "../map/components/MapOfferSidebarQuickView";
import {IMapOfferSidebar} from "../map/types/MapOfferSidebar";
import {hitGTMPropertySelectItem} from "../tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {hitOfferModalArrowGtm} from "../tracking/offer_modal_arrow_gtm_events";
import {IOfferDetail} from "../types/IOfferDetail";

interface IProps {
    offer: IMapOfferSidebar;
    latestQuery: ILatestQuery;
    propertyList: IPropertyListObj;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
    gtmMeta: {
        listId: string;
        listName: string;
    };
}

export const OfferSidebarPropertiesList = (props: IProps) => {
    const {properties, filters, pageCount, onPageChange: onPropertyListPageChange, onSortChange, sort, page, requestState, pageIsLoading} = props.propertyList;
    const propertiesRequestState = useSelector((store: IRPStore) => store.offerDetail.properties.modal.requestState);
    const mapListRequestState = useSelector((store: IRPStore) => store.offerListMap.list.requestState);

    const {isMobile} = useUserDevice();
    const theme = useTheme();

    // QuickView Variant
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    useEffect(() => {
        // QuickView Variant
        //Close QW if user change property
        if (isQuickViewOpen) {
            setIsQuickViewOpen(false);
        }

        //When filters are changing set current page from n to 1
        if (page.currentPage !== 1) {
            page.setCurrentPage(1);
        }
    }, [props.offer.id, mapListRequestState]);

    const onPageChange = (newPage: number) => {
        const direction = newPage > page.currentPage ? "right" : "left";
        const listType = props.propertyListType === PropertyListType.TILES ? "tiles" : "table";

        hitOfferModalArrowGtm(direction, listType);

        return onPropertyListPageChange(newPage, filters.currentFilters);
    };

    const handleQuickViewOpen = (index: number) => {
        const currentOffer = props.offer;
        const currentProperty = properties[index];

        setIsQuickViewOpen(true);
        setSelectedIndex(index);
        openQuickViewHit(currentOffer.id, currentProperty.id);

        hitGTMPropertySelectItem({
            offer: currentOffer,
            property: currentProperty,
            ...props.gtmMeta
        });
    };

    const handleQuickViewClose = (): void => {
        setIsQuickViewOpen(false);
        setSelectedIndex(null);
        closeQuickViewHit();
    };

    const handleQuickViewOpenByProperty = (property: IPropertyListProperty) => {
        const index = properties.findIndex(({id}) => id === property.id);
        if (index !== -1) {
            handleQuickViewOpen(index);
        }
    };

    return (
        <>
            <PropertyListTableHolder isQuickViewOpen={isQuickViewOpen}>
                <div css={controlsWrapperStyle}>
                    <MapOfferPropertyFilters offerType={props.offer.type} propertyList={props.propertyList} />
                    <PropertyListTabSwitcher
                        currentPropertyListType={props.propertyListType}
                        setCurrentPropertyListType={props.onPropertyListTypeClick}
                        buttonCss={listTabSwitchButtonStyle}
                        css={propertyListTabSwitcherStyle}
                    />
                </div>
                {properties.length > 0 && (
                    <>
                        {props.propertyListType === PropertyListType.TABLE && (
                            <PropertyListTable
                                requestState={requestState}
                                offer={props.offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value)}
                                onPageChange={onPageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                css={[modalPropertyList]}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleDivClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground={theme.colors.gray[100]}
                                gtmMeta={props.gtmMeta}
                            />
                        )}

                        {props.propertyListType === PropertyListType.TILES && (
                            <PropertyListTileList
                                requestState={requestState}
                                properties={properties}
                                offer={props.offer as unknown as IOfferDetail}
                                css={modalPropertyList}
                                gridLayoutCols={{md: 2, lg: 2}}
                                onPropertyTileClick={handleQuickViewOpenByProperty}
                                propertyBoxComponent="div"
                                gtmMeta={props.gtmMeta}
                            />
                        )}

                        {pageCount > 0 && (
                            <div css={paginationHolder}>
                                <Pagination
                                    onPageChange={onPageChange}
                                    pageCount={pageCount}
                                    currentPage={page.currentPage}
                                    size={isMobile ? PaginationSize.SM : PaginationSize.MD}
                                    siblingsCount={isMobile ? 0 : 1}
                                />
                            </div>
                        )}
                    </>
                )}
                {properties?.length === 0 && requestState === RequestState.Success && (
                    <Text variant="body_copy_0" align="center" css={noResultsWrapperStyle}>
                        Brak wyników dla szukanej frazy
                    </Text>
                )}
            </PropertyListTableHolder>
            {/*QuickView Variant*/}
            {isQuickViewOpen && selectedIndex !== null && (
                <MapOfferSidebarQuickView
                    handleQuickViewClose={handleQuickViewClose}
                    propertyList={props.propertyList}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    propertiesRequestState={propertiesRequestState}
                    offer={props.offer}
                />
            )}
        </>
    );
};

const controlsWrapperStyle = css`
    ${p(2, 0)};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${calculateRemSize(4)};
`;

const listTabSwitchButtonStyle = css`
    ${p(1)};
`;

const PropertyListTableHolder = styled.div<{isQuickViewOpen: boolean}>`
    flex-grow: 1;
    flex-shrink: 1;
    overflow-x: hidden;
    padding-top: 1.5rem;
    ${prettyScroll()};
    ${({isQuickViewOpen}) =>
        isQuickViewOpen &&
        css`
            display: none;
        `};
`;

const modalPropertyList = css`
    margin-bottom: 0 !important;
`;

const propertyListTabSwitcherStyle = css`
    flex: 0 0 auto;
`;

const paginationHolder = (theme: Theme) => css`
    display: flex;
    justify-content: center;
    background-color: ${theme.colors.gray[100]};
    width: 100%;
    padding: 1.6rem;
    flex-shrink: 0;
    z-index: 1;
    position: relative;

    @media (min-width: ${theme.breakpoints.sm}) {
        position: sticky;
        bottom: 0;
        padding: 0.6rem 0 0;
    }
`;

const noResultsWrapperStyle = css`
    ${p(6, 2, 2, 2)};
`;
